import { useEffect, useState } from 'react';
import {
  CONFIDENCE_LEVELS,
  COMPASS_WARNING,
  DIP_DIRECTIONS,
  FEATURE_TYPES,
  READING_FORMAT_CONFIRMED,
  READING_FORMAT_NOTE,
  formatReading,
  impliedDipDirection,
  type ReadingSource,
} from '@kilroy/shared';
import { IconCompass } from '../icons';

/* Structural readings.
 *
 * The load-bearing control on this screen is the reading-source toggle, and it
 * exists because of a domain fact, not a UI preference: phone magnetometers
 * read garbage on mineralized ground, and mineralized ground is the entire
 * point of the survey. A VMS target is iron oxide and sulfide over an
 * aeromagnetic anomaly — the exact conditions that make a phone compass lie.
 *
 * So the Brunton is the default and the primary, the sensor is offered only as
 * a PREFILL with the warning attached, and reading_source is required to save.
 * Getting this wrong is cheap to avoid now and expensive to retrofit once
 * there are two field seasons of readings with no provenance on them.
 */

export interface StructuralDraft {
  feature_type: string;
  strike: number;
  dip: number;
  dip_direction: string;
  reading_source: ReadingSource | null;
  offset_sense?: string;
  confidence: 'high' | 'medium' | 'low';
}

const SOURCES: { id: ReadingSource; label: string; hint: string }[] = [
  { id: 'brunton', label: 'Brunton', hint: 'Instrument' },
  { id: 'sensor', label: 'Phone sensor', hint: 'Prefill only' },
  { id: 'estimate', label: 'Estimate', hint: 'Eyeballed' },
];

/** Live compass heading, when the device offers one and the user has allowed
 *  it. Used only to prefill — never written without an explicit source choice. */
function useHeading(active: boolean): number | null {
  const [heading, setHeading] = useState<number | null>(null);

  useEffect(() => {
    if (!active) return;
    const onOrient = (e: DeviceOrientationEvent) => {
      const webkit = (e as unknown as { webkitCompassHeading?: number })
        .webkitCompassHeading;
      if (typeof webkit === 'number') setHeading(webkit);
      else if (e.alpha != null) setHeading((360 - e.alpha) % 360);
    };
    window.addEventListener('deviceorientation', onOrient);
    return () => window.removeEventListener('deviceorientation', onOrient);
  }, [active]);

  return heading;
}

function Stepper({
  label,
  value,
  min,
  max,
  wrap,
  onChange,
  suffix = '°',
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  wrap?: boolean;
  onChange: (v: number) => void;
  suffix?: string;
}) {
  const clamp = (v: number) => {
    if (wrap) return ((v % (max + 1)) + (max + 1)) % (max + 1);
    return Math.min(max, Math.max(min, v));
  };

  return (
    <div className="stepper">
      <div className="label label--plain">{label}</div>
      <div className="stepper__row">
        <button type="button" className="stepper__btn" onClick={() => onChange(clamp(value - 10))} aria-label={`${label} minus 10`}>
          −10
        </button>
        <button type="button" className="stepper__btn" onClick={() => onChange(clamp(value - 1))} aria-label={`${label} minus 1`}>
          −
        </button>

        <div className="stepper__value">
          <input
            type="number"
            inputMode="numeric"
            value={value}
            min={min}
            max={max}
            onChange={(e) => onChange(clamp(Number(e.target.value)))}
            aria-label={label}
          />
          <span aria-hidden="true">{suffix}</span>
        </div>

        <button type="button" className="stepper__btn" onClick={() => onChange(clamp(value + 1))} aria-label={`${label} plus 1`}>
          +
        </button>
        <button type="button" className="stepper__btn" onClick={() => onChange(clamp(value + 10))} aria-label={`${label} plus 10`}>
          +10
        </button>
      </div>
    </div>
  );
}

export function StructuralPanel({
  draft,
  onChange,
  showError,
}: {
  draft: StructuralDraft;
  onChange: (next: StructuralDraft) => void;
  showError: boolean;
}) {
  const heading = useHeading(draft.reading_source === 'sensor');
  const set = (patch: Partial<StructuralDraft>) => onChange({ ...draft, ...patch });

  return (
    <>
      <div className="field">
        <div className="label">Feature</div>
        <div className="chiprow">
          {FEATURE_TYPES.map((f) => (
            <button
              key={f.id}
              type="button"
              className="chip"
              aria-pressed={draft.feature_type === f.id}
              onClick={() => set({ feature_type: f.id })}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <div className="label">Reading</div>

        {/* The reading as it will be stored. Shown live so nobody discovers the
            format after the fact. */}
        <div className="reading">
          <span className="reading__value mono">
            {formatReading({
              strike: draft.strike,
              dip: draft.dip,
              dip_direction: draft.dip_direction,
            })}
          </span>
          {!READING_FORMAT_CONFIRMED && (
            <span className="reading__caveat mono">format unconfirmed</span>
          )}
        </div>

        <Stepper
          label="Strike"
          value={draft.strike}
          min={0}
          max={359}
          wrap
          onChange={(strike) =>
            set({ strike, dip_direction: draft.dip_direction || impliedDipDirection(strike) })
          }
        />
        <Stepper label="Dip" value={draft.dip} min={0} max={90} onChange={(dip) => set({ dip })} />

        <div className="label label--plain" style={{ marginTop: 'var(--s-3)' }}>
          Dips toward
        </div>
        <div className="chiprow">
          {DIP_DIRECTIONS.map((d) => (
            <button
              key={d}
              type="button"
              className="chip"
              aria-pressed={draft.dip_direction === d}
              onClick={() => set({ dip_direction: d })}
            >
              {d}
            </button>
          ))}
        </div>
        {!READING_FORMAT_CONFIRMED && <p className="field__hint">{READING_FORMAT_NOTE}</p>}
      </div>

      {/* ── Reading source. Required. ─────────────────────────────────────── */}
      <div className={`field${showError && !draft.reading_source ? ' field--error' : ''}`}>
        <div className="label">Where did this reading come from?</div>

        <div className="sourcegrid">
          {SOURCES.map((s) => (
            <button
              key={s.id}
              type="button"
              className="source"
              aria-pressed={draft.reading_source === s.id}
              onClick={() => set({ reading_source: s.id })}
            >
              {s.id === 'brunton' && <IconCompass size={22} />}
              <span className="source__label">{s.label}</span>
              <span className="source__hint mono">{s.hint}</span>
            </button>
          ))}
        </div>

        {/* Verbatim from the contract. It appears whenever the sensor is in
            play, not once at onboarding — the warning has to be next to the
            mistake. */}
        <p className="warning">{COMPASS_WARNING}</p>

        {draft.reading_source === 'sensor' && (
          <div className="prefill">
            <span className="mono">
              {heading == null ? 'No compass signal' : `Compass reads ${Math.round(heading)}°`}
            </span>
            <button
              type="button"
              className="btn btn--sand"
              disabled={heading == null}
              onClick={() => heading != null && set({ strike: Math.round(heading) })}
            >
              Use as strike
            </button>
          </div>
        )}

        {showError && !draft.reading_source && (
          <p className="field__error">Pick where this reading came from before saving.</p>
        )}
      </div>

      <div className="field">
        <div className="label">Confidence</div>
        <div className="chiprow">
          {CONFIDENCE_LEVELS.map((c) => (
            <button
              key={c.id}
              type="button"
              className="chip"
              aria-pressed={draft.confidence === c.id}
              onClick={() => set({ confidence: c.id })}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
